"use client";

import { XIcon, CheckCircleIcon } from "lucide-react";
import { BellRingingIcon } from "@phosphor-icons/react";
import Modal from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import { useI18N } from "common/hooks";
import { useRef, useEffect } from "react";

// ============================================================================
// Types
// ============================================================================

interface NotificationSuccessModalProps {
  businessName: string;
  notifyAccessibilityInformation: boolean;
  notifyLiveAlert: boolean;
  isOpen: boolean;
  onClose: () => void;
}

// ============================================================================
// Component
// ============================================================================

export function NotificationSuccessModal({
  businessName,
  notifyAccessibilityInformation,
  notifyLiveAlert,
  isOpen,
  onClose,
}: NotificationSuccessModalProps) {
  const i18n = useI18N();
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Set initial focus on close button when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        closeButtonRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Only show enabled notifications in the list
  const enabledNotifications = [];
  if (notifyAccessibilityInformation) {
    enabledNotifications.push({
      key: "newAccessibilityInformation",
      label: i18n.get("newAccessibilityInformation"),
    });
  }
  if (notifyLiveAlert) {
    enabledNotifications.push({
      key: "liveAccessibilityAlerts",
      label: i18n.get("liveAccessibilityAlerts"),
    });
  }

  return (
    <Modal
      focusTrapped
      center
      blockScroll={true}
      animationDuration={0}
      open={isOpen}
      onClose={onClose}
      classNames={{
        modal:
          "bg-[transparent] p-0 focus:outline-0 shadow-none w-[calc(100%-32px)] sm:w-[calc(100%-64px)] md:w-[580px] max-h-[90vh]",
        closeButton: "hidden",
      }}
    >
      <section
        className="bg-bg-brand-secondary-default rounded-xl overflow-hidden w-full max-h-[90vh] flex flex-col"
        aria-labelledby="modal-title"
      >
        {/* Sticky Header */}
        <header className="sticky top-0 z-[2] bg-bg-brand-secondary-default border-b border-border-token-brand-minimal py-2 px-0 flex items-center justify-between rounded-t-xl">
          <div className="flex items-center justify-center w-11 h-11 shrink-0">
            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              className="flex items-center justify-center min-w-[40px] min-h-[40px] p-2 rounded-lg hover:bg-bg-brand-secondary-hover transition-colors focus-blue"
              aria-label="Close modal"
            >
              <XIcon
                size={24}
                className="text-fg-neutral-strong"
                aria-hidden="true"
              />
            </button>
          </div>
          <h1
            id="modal-title"
            className="flex-1 text-center text-fg-neutral-strong title-s-semibold sm:title-s-semibold leading-6 sm:leading-7"
          >
            {i18n.get("notifyme")}
          </h1>
          <div className="w-11 h-11 shrink-0" aria-hidden="true" />
        </header>

        {/* Lightbox Content */}
        <div className="relative z-[1] bg-bg-brand-secondary-default px-4 sm:px-6 py-3 overflow-x-hidden overflow-y-auto rounded-b-xl flex gap-4 lg:gap-6">
          {/* Main Content Section */}
          <div className="flex-1 flex flex-col gap-3 py-2 sm:py-3">
            {/* Success InfoIcon Box */}
            <div className="flex gap-3 sm:gap-4 items-center rounded-lg px-3 sm:px-4 py-2 bg-container-success-minimal border border-border-token-success-subtle">
              <CheckCircleIcon
                size={20}
                className="text-fg-success-subtle shrink-0 sm:w-6 sm:h-6"
                aria-hidden="true"
              />
              <div className="flex-1">
                <p className="body-l-semibold text-fg-neutral-strong">
                  {i18n.get("yourPreferencesHaveBeenSaved") ||
                    "Your preferences has been saved."}
                </p>
              </div>
            </div>

            {/* Notification Details */}
            <div className="flex flex-col">
              {/* Header Text */}
              <div className="py-3">
                <p className="label-m-medium text-fg-neutral-strong">
                  {i18n.get("willnotifyyouwhen")}
                  <span className="text-fg-neutral-strong font-bold">
                    {businessName}
                  </span>
                  {" has:"}
                </p>
              </div>

              {/* Notification List */}
              {enabledNotifications.length > 0 && (
                <ul className="flex flex-col gap-2 sm:gap-3 py-2 overflow-x-hidden overflow-y-auto rounded-b-xl">
                  {enabledNotifications.map((notification) => (
                    <li
                      key={notification.key}
                      className="flex gap-3 sm:gap-4 items-center w-full"
                    >
                      <div className="flex items-center h-5 sm:h-6 shrink-0">
                        <BellRingingIcon
                          size={20}
                          weight="regular"
                          className="text-fg-neutral-strong w-5 h-5 sm:w-6 sm:h-6"
                          aria-hidden="true"
                        />
                      </div>
                      <div className="flex-1 flex flex-col justify-center min-h-[24px]">
                        <p className="label-m-medium text-fg-neutral-strong">
                          {notification.label}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Vertical Divider - Hidden on mobile, shown on desktop */}
          <div className="hidden lg:flex items-center justify-center shrink-0">
            <div className="h-full w-px border border-border-token-neutral-minimal" />
          </div>

          {/* Mobile App Download Section - Hidden on mobile, shown on desktop */}
          <div className="hidden lg:flex flex-col gap-3 items-center justify-center w-[230px] shrink-0">
            {/* QR Code Placeholder */}
            <div className="w-[120px] h-[120px] bg-container-brand-minimal rounded-lg flex items-center justify-center">
              <div className="w-[100px] h-[100px] bg-white rounded">
                <img
                  src="/images/Sociability_App_Download_QR_Code.png"
                  alt={i18n.get("scanQrCodeToDownloadApp")}
                  className="w-full h-full"
                />
              </div>
            </div>

            {/* Download Text */}
            <div className="flex flex-col items-center text-center w-full">
              <h2 className="title-s-semibold leading-7 text-fg-neutral-strong mb-1">
                {i18n.get("downloadToMobile") || "Download to mobile"}
              </h2>
              <p className="label-s-medium leading-4 text-fg-neutral-strong">
                {i18n.get("pushNotificationsNotAvailableOnWeb") ||
                  "Push notifications aren't available on web yet â€” download the app to get live alerts."}
              </p>
            </div>
          </div>
        </div>
      </section>
    </Modal>
  );
}

export default NotificationSuccessModal;
